<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { get } from 'svelte/store';

    import {
        INPUT_TIPS_NORMAL_ICON,
        PICTURE_LOADING_IMAGE,
        RETRY_ICON
    } from './constants/loginImage';
    import { getFifaLoginFlowContext } from './context';
    import { MODULE_ENUM } from './enums/loginModule';
    import {
        changeModule,
        pageModuleEnterTime,
        setLoginFlowLoading
    } from './store/loginFlow';
    import Toast from './Toast.svelte';
    import { handleKeyboardActivate } from './utils/a11y';
    import {
        genCaptchaApi,
        getPassportError,
        verifyCaptchaApi
    } from './utils/loginPassport';
    import { getModuleDuration, getErrorTrackAttrs } from './utils/trackHelper';

    import webp from '@/actions/webp';

    const { bids, trackEvent } = getFifaLoginFlowContext();

    let isLoading = false;
    let pictureUrl = '';
    let codeValues = ['', '', '', ''];
    let codeInput = '';
    let toastRefs: Toast | null = null;
    const codeInputRefs: (HTMLInputElement | null)[] = [null, null, null, null];
    let moduleEnterTime = 0;

    $: codeInput = codeValues.join('');

    $: {
        if (codeInput.length === 4) {
            trackEvent(bids.dvCodeTyp);
            handleVerifyCaptcha();
        }
    }

    const showToast = (message: string) => {
        toastRefs?.show(message, { type: 'error' });
    };

    const handleResetCode = () => {
        codeValues = ['', '', '', ''];
    };

    const handleCodeFocus = (index: number, event?: Event) => {
        const input = event
            ? (event.currentTarget as HTMLInputElement)
            : codeInputRefs[index];

        if (!input) {
            return;
        }

        requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        });
    };

    const handleCodeInputClick = (index: number, event: Event) => {
        trackEvent(bids.dvCodeCk, { button_name: 'input_box' });
        handleCodeFocus(index, event);
    };

    const handleCodeInput = (index: number, event: Event) => {
        const input = event.target as HTMLInputElement;
        const value = input.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 1);

        codeValues[index] = value;
        input.value = value;

        if (value && index < 3) {
            codeInputRefs[index + 1]?.focus();
        }
    };

    const handleCodeKeyDown = (index: number, event: KeyboardEvent) => {
        if (event.key === 'Backspace' && !codeValues[index] && index > 0) {
            codeInputRefs[index - 1]?.focus();
        }
    };

    const handleCodePaste = (event: ClipboardEvent) => {
        event.preventDefault();
        const pastedText = event.clipboardData?.getData('text') || '';
        const cleanText = pastedText.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4);

        for (let i = 0; i < 4; i++) {
            codeValues[i] = cleanText[i] || '';

            if (codeInputRefs[i]) {
                codeInputRefs[i]!.value = codeValues[i];
            }
        }

        codeInputRefs[Math.min(cleanText.length, 3)]?.focus();
    };

    const handleGetCaptcha = () => {
        if (isLoading) {
            return;
        }

        isLoading = true;
        handleResetCode();
        const startTime = window.performance.now();

        genCaptchaApi()
            .then((res) => {
                pictureUrl = `data:image/png;base64,${res.response.captcha}`;
                trackEvent(bids.dvCodeImageSw);
                trackEvent(bids.dvCodeImageLoadingTimeSw, {
                    duration: getModuleDuration(startTime)
                });
            })
            .catch((err) => {
                const response = getPassportError(err);

                if (response?.errno === 53001) {
                    showToast(
                        'Demasiados intentos. Espera un poco antes de volver a intentar.'
                    );
                } else {
                    showToast(
                        'Error al obtener el código. Por favor, haz clic para recibir uno nuevo'
                    );
                }

                pictureUrl = '';
            })
            .finally(() => {
                isLoading = false;
            });
    };

    const handleRefreshCaptcha = () => {
        trackEvent(bids.dvCodeCk, { button_name: 'change_dv_code' });
        handleGetCaptcha();
    };

    const handleVerifyCaptcha = () => {
        setLoginFlowLoading(true);

        verifyCaptchaApi(codeInput)
            .then(() => {
                trackEvent(bids.dvCodeBt, { is_dv_code_reguest_success: 1 });
                trackEvent(bids.dvCodeJumpBt, { is_jump_success: 1 });
                changeModule(MODULE_ENUM.CODE);
            })
            .catch((err) => {
                const response = getPassportError(err);
                const errorAttrs = getErrorTrackAttrs(response);

                trackEvent(bids.dvCodeBt, {
                    is_dv_code_reguest_success: 0,
                    ...errorAttrs
                });
                trackEvent(bids.dvCodeJumpBt, {
                    is_jump_success: 0,
                    ...errorAttrs
                });

                if (response?.errno === 53001) {
                    showToast(
                        'Demasiados intentos. Espera un poco antes de volver a intentar.'
                    );
                } else {
                    showToast(
                        'Entrada no válida. No te preocupes, intenta de nuevo, ¡falta poco!'
                    );
                }

                handleCodeFocus(0);
                handleGetCaptcha();
            })
            .finally(() => {
                setLoginFlowLoading(false);
            });
    };

    onMount(() => {
        moduleEnterTime = get(pageModuleEnterTime) || window.performance.now();
        trackEvent(bids.dvCodeLoadingTimeSw, {
            duration: getModuleDuration(moduleEnterTime)
        });
        handleGetCaptcha();
        trackEvent(bids.dvCodeSw);
        trackEvent(bids.dvCodeFullSw);
    });

    onDestroy(() => {
        trackEvent(bids.dvCodeTimeEx);
    });
</script>

<div class="picture-module">
    <div class="title">
        Por tu seguridad, necesitamos confirmar que eres una persona real antes de
        enviarte el código por SMS.
    </div>
    <div
        class="image-wrapper"
        role="button"
        tabindex="0"
        on:click={handleRefreshCaptcha}
        on:keydown={(event) => handleKeyboardActivate(event, handleRefreshCaptcha)}
    >
        {#if isLoading}
            <div
                class="loading"
                style={`background-image:url(${PICTURE_LOADING_IMAGE});`}
            ></div>
        {:else if pictureUrl}
            <img class="image" src={pictureUrl} alt="" />
        {:else}
            <div class="picture-loading-error">
                <div class="tips-icon" use:webp={INPUT_TIPS_NORMAL_ICON}></div>
                <div class="tips-text">
                    No se ha podido obtener el código de verificación. Haga clic en el
                    botón para obtener un nuevo código de verificación.
                </div>
            </div>
        {/if}
    </div>
    <div
        class="retry-wrapper"
        role="button"
        tabindex="0"
        on:click={handleRefreshCaptcha}
        on:keydown={(event) => handleKeyboardActivate(event, handleRefreshCaptcha)}
    >
        <div class="retry-icon" use:webp={RETRY_ICON}></div>
        {#if !isLoading && !pictureUrl}
            Toca para cambiarlas
        {:else}
            Solicitar un nuevo código de verificación
        {/if}
    </div>
    <div class="input-tips">Escribe las letras que ves en la imagen</div>
    <div class="input-wrapper" on:paste={handleCodePaste}>
        {#each codeValues as _, index}
            <input
                type="text"
                class="code-input"
                class:is-input={codeValues[index]}
                maxlength="1"
                bind:this={codeInputRefs[index]}
                value={codeValues[index]}
                on:focus={(event) => handleCodeFocus(index, event)}
                on:click={(event) => handleCodeInputClick(index, event)}
                on:input={(event) => handleCodeInput(index, event)}
                on:keydown={(event) => handleCodeKeyDown(index, event)}
            />
        {/each}
    </div>
    <div class="captcha-tips">
        Cuatro letras (sin distinción de mayúsculas y minúsculas)
    </div>
</div>

<Toast bind:this={toastRefs} />

<style lang="scss">
    .picture-module {
        padding: 0 0.6rem;

        .title {
            margin-bottom: 0.6rem;
            font-family: DiDiSans-Pro-Medium;
            font-size: 0.32rem;
            line-height: 0.4rem;
            color: #0c0c0d;
        }

        .image-wrapper {
            position: relative;
            width: 100%;
            height: 2rem;
            border-radius: 0.32rem;
            background: #f2f2f5;
            overflow: hidden;
            cursor: pointer;
        }

        .loading {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 1.28rem;
            height: 1.28rem;
            background-size: 100% 100%;
        }

        .image {
            width: 100%;
            height: 100%;
            object-fit: fill;
        }

        .picture-loading-error {
            display: flex;
            height: 100%;
            padding: 0 0.68rem;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        }

        .tips-icon {
            width: 0.4rem;
            height: 0.4rem;
            background-size: 100% 100%;
            margin-bottom: 0.12rem;
        }

        .tips-text {
            font-size: 0.24rem;
            text-align: center;
            color: #0c0c0d;
        }

        .retry-wrapper {
            margin: 0.24rem auto 0.4rem;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 0.28rem;
            color: #3355ff;
            cursor: pointer;
        }

        .retry-icon {
            width: 0.28rem;
            height: 0.28rem;
            background-size: 100% 100%;
            margin-right: 0.08rem;
        }

        .input-tips {
            font-size: 0.28rem;
            text-align: center;
            color: #0c0c0d;
            margin: 0 auto 0.24rem;
        }

        .input-wrapper {
            display: flex;
            gap: 0.5rem;
            margin-bottom: 0.4rem;

            .code-input {
                width: 1.2rem;
                height: 1rem;
                border: none;
                border-bottom: 0.02rem solid #bababf;
                border-radius: 0;
                -webkit-appearance: none;
                appearance: none;
                text-align: center;
                font-family: DiDiSans-Pro-Bold;
                font-size: 0.6rem;
                line-height: 0.76rem;
                color: #0c0c0d;
                background: #ffffff;
                outline: none;

                &:focus {
                    border-color: #0c0c0d;
                    caret-color: #0c0c0d;
                }
            }

            .is-input {
                border-color: #0c0c0d;
            }
        }

        .captcha-tips {
            margin: 0.2rem auto 0;
            font-size: 0.24rem;
            color: #8a8a91;
        }
    }
</style>
